import type { CardStackSummary, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import type { LessonModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import { useMemo } from "react"
import InstructionalLesson from "./unified-lesson-editable/InstructionalLesson"
import NonInstructionalLesson from "./unified-lesson-editable/NonInstructionalLesson"

interface Props {
  courseDate: CourseDateCombined
  course: Course
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  units: Array<CardStackSummary> | null
  rotationCalendar: RotationCalendar
  planbook: Planbook
  /** Opens the edit-course modal for the given courseId. */
  editCourseTitleAndOrder: (courseId: string) => void
  lessonsCollapsed: boolean
  perspective: "day" | "week" | "month"
  isFullScreen?: boolean
  /** No drag pad, and no drag: a lesson shown out of its calendar has nowhere to move to. */
  dragDisabled?: boolean
}

/** One course's slot on one day. A non-instructional course has no lesson to plan. */
export default tracked(function UnifiedLessonEditable({
  courseDate,
  course,
  lesson,
  units,
  rotationCalendar,
  planbook,
  editCourseTitleAndOrder,
  lessonsCollapsed,
  isFullScreen,
  dragDisabled,
}: Props) {
  const modelsNeededForFirstEdit: LessonModelsNeededForFirstEdit = useMemo(
    () => ({
      cardStack: lesson,
      dateString: courseDate.dateString,
      planbook,
      course,
      rotationCalendar,
    }),
    [lesson, courseDate.dateString, planbook, course, rotationCalendar],
  )

  if (course.attributes.isNonInstructional) {
    return (
      <NonInstructionalLesson
        lesson={lesson}
        course={course}
        courseDate={courseDate}
        units={units}
        planbook={planbook}
        rotationCalendar={rotationCalendar}
        dateString={courseDate.dateString}
        modelsNeededForFirstEdit={modelsNeededForFirstEdit}
        editCourseTitleAndOrder={editCourseTitleAndOrder}
      />
    )
  } else {
    return (
      <InstructionalLesson
        courseDate={courseDate}
        lesson={lesson}
        units={units}
        course={course}
        planbook={planbook}
        rotationCalendar={rotationCalendar}
        modelsNeededForFirstEdit={modelsNeededForFirstEdit}
        editCourseTitleAndOrder={editCourseTitleAndOrder}
        lessonsCollapsed={lessonsCollapsed}
        isFullScreen={isFullScreen}
        dragDisabled={dragDisabled}
      />
    )
  }
})
